import * as React from "react";
import { I18n } from "react-redux-i18n";

import { PedigreeExternalLinkDto } from "@modules/pedigrees/dto/pedigree/external-link/get-dto";
import { PedigreeListDto } from "@common/dto/pedigree/list-dto";
import { Loading } from "@common/components/loading";
import { HttpErrorDto } from "@common/dto/http";
import { getTestAttributes } from "@common/utils/tests";
import { MODULE_NAME } from "@modules/individuals";

import "./generate-external-link.styles.scss";

export type GenerateExternalLinkProps = {
  showErrorMessage: (msg: string) => void;
  getData: (id: PedigreeListDto['id']) => Promise<PedigreeExternalLinkDto>;
  selectedPedigreeId: PedigreeListDto['id'];
};

export const GenerateExternalLink: React.FC<GenerateExternalLinkProps> = (props) => {
  const { getData, showErrorMessage, selectedPedigreeId } = props;
  const [url, setUrl] = React.useState<PedigreeExternalLinkDto>();
  const [loading, setLoading] = React.useState(true);

  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  const getPedigreeExternalLink = React.useCallback(() => {
    setLoading(true);
    getData(selectedPedigreeId)
      .then(setUrl)
      .catch((err) => {
        console.error(err);
        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to get generated external link");
        showErrorMessage(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedPedigreeId, showErrorMessage, getData]);

  const handleSelectText = React.useCallback(() => {
    setTimeout(() => {
      inputRef.current?.select();
    }, 0);
  }, []);

  const generatedExternalFullUrl = React.useMemo(() => {
    const path = `${window.location.pathname.endsWith('/') ? '' : '/'}${url || ''}`;
    return `${window.location.origin}${window.location.pathname}${path}`
  }, [url]);

  React.useEffect(() => {
    getPedigreeExternalLink();
  }, []);

  return (
    <div className="GenerateExternalLink">
      {loading ? (
        <div className="GenerateExternalLink__loading">
          <Loading size={25} />
        </div>
      ) : (
        <>
          <span className="GenerateExternalLink__label">
            {I18n.t("Press Ctrl+C to copy URL to clipboard")}:
          </span>
          <textarea
            {...getTestAttributes(
              MODULE_NAME,
              "generate-external-link-value",
              "textarea"
            )}
            ref={inputRef}
            autoFocus
            onFocus={handleSelectText}
            readOnly
            className="GenerateExternalLink__value"
            value={generatedExternalFullUrl}
          />
        </>
      )}
    </div>
  );
};
